import React, { use, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import "./AdminPanel.css";

const API_GET_URL            = `${process.env.REACT_APP_ADMIN_API_URL}/assigned-workers`;
const API_POST_URL           = `${process.env.REACT_APP_ADMIN_API_URL}/assign-worker`;
const API_PDFS_URL           = `${process.env.REACT_APP_ADMIN_API_URL}/uploaded-bundles`;
const API_WORKER_DETAILS_URL = `${process.env.REACT_APP_ADMIN_API_URL}/worker-details`;
const API_UPDATE_WORKER_URL  = `${process.env.REACT_APP_ADMIN_API_URL}/update-worker-info`;

export const AdminPanel = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      alert("Login First");
      navigate("/adminP");
    }
  }, []); // ✅ runs only once

  const [assignedData,       setAssignedData]       = useState([]);
  const [pdfs,               setPdfs]               = useState([]);
  const [loading,            setLoading]            = useState(false);
  const [pdfsLoading,        setPdfsLoading]        = useState(false);
  const [error,              setError]              = useState("");
  const [showAssignForm,     setShowAssignForm]      = useState(false);
  const [submitLoading,      setSubmitLoading]       = useState(false);
  const [excelDownloading,   setExcelDownloading]   = useState(false);
  const [personsDownloading, setPersonsDownloading] = useState(false);

  const [expandedRow,    setExpandedRow]    = useState(null);
  const [workerDetails,  setWorkerDetails]  = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError,   setDetailsError]   = useState("");

  const [editMode,       setEditMode]       = useState(false);
  const [editedPdfs,     setEditedPdfs]     = useState([]);
  const [newPdfRange,    setNewPdfRange]    = useState({ start: "", end: "" });
  const [rangeError,     setRangeError]     = useState("");
  const [rangeInputs,    setRangeInputs]    = useState({ startPdf: "", endPdf: "" });
  const [generatedRange, setGeneratedRange] = useState([]);

  // Editable worker info state
  const [editedWorkerName,   setEditedWorkerName]   = useState("");
  const [editedPhoneNumber,  setEditedPhoneNumber]  = useState("");
  const [workerInfoError,    setWorkerInfoError]    = useState("");

  // Separate loading states for independent updates
  const [workerUpdateLoading, setWorkerUpdateLoading] = useState(false);
  const [pdfUpdateLoading, setPdfUpdateLoading] = useState(false);
  const [workerUpdateMessage, setWorkerUpdateMessage] = useState("");
  const [pdfUpdateMessage, setPdfUpdateMessage] = useState("");

  const [formData, setFormData] = useState({
    workerName:   "",
    phoneNumber:  "",
    pdfCount:     0,
    assignedPdfs: [],
    status:       "Assigned",
  });

  // ── Initial data fetch ─────────────────────────────────────────
  useEffect(() => {
    fetchAssignedWorkers();
    fetchPdfs();
  }, []);

  useEffect(() => {
    const fetchPdfsNumber = async () => {
      try {
        await axios.get(
          `${process.env.REACT_APP_ADMIN_API_URL}/fetchpdfcount`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          }
        );
      } catch (error) {
        console.log(error);
      }
    };
    fetchPdfsNumber();
  });

  // ── Auto-generate range when assign-form inputs change ─────────
  useEffect(() => {
    if (rangeInputs.startPdf && rangeInputs.endPdf) {
      const startNum = parseInt(rangeInputs.startPdf, 10);
      const endNum   = parseInt(rangeInputs.endPdf,   10);
      if (isNaN(startNum) || isNaN(endNum)) {
        setRangeError("Please enter valid numbers");
        setGeneratedRange([]);
        return;
      }
      if (startNum > endNum) {
        setRangeError("Start must be ≤ End");
        setGeneratedRange([]);
        return;
      }
      const range = [];
      for (let i = startNum; i <= endNum; i++) range.push(i.toString());
      setRangeError("");
      setGeneratedRange(range);
      setFormData(prev => ({ ...prev, assignedPdfs: range, pdfCount: range.length }));
    } else {
      setGeneratedRange([]);
      setRangeError("");
      setFormData(prev => ({ ...prev, assignedPdfs: [], pdfCount: 0 }));
    }
  }, [rangeInputs]);

  // ── Validate edit-mode range inputs ───────────────────────────
  useEffect(() => {
    if (newPdfRange.start && newPdfRange.end) {
      const s = parseInt(newPdfRange.start);
      const e = parseInt(newPdfRange.end);
      setRangeError(s > e ? "Start must be ≤ End" : "");
    } else {
      setRangeError("");
    }
  }, [newPdfRange]);

  // ══════════════════════════════════════════════════════════════
  //  Fetch helpers
  // ══════════════════════════════════════════════════════════════
  const fetchAssignedWorkers = async () => {
    try {
      setLoading(true);
      setError("");
      const res  = await axios.get(API_GET_URL,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          });
      const data = Array.isArray(res.data) ? res.data : [];

      const grouped = Object.values(
        data.reduce((acc, item) => {
          const key = item.worker_name + "_" + item.phone_number;
          if (!acc[key]) {
            acc[key] = { ...item, assigned_bundles: [item.assigned_bundles] };
          } else {
            acc[key].assigned_bundles.push(item.assigned_bundles);
          }
          return acc;
        }, {})
      );

      setAssignedData(grouped);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const fetchPdfs = async () => {
    try {
      setPdfsLoading(true);
      const res  = await axios.get(API_PDFS_URL,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          });
      const nums = (Array.isArray(res.data) ? res.data : [])
        .map(p => p.bundle_name?.replace(".pdf", "") || "")
        .filter(n => !isNaN(n) && n !== "")
        .sort((a, b) => parseInt(a) - parseInt(b));
      setPdfs(nums);
    } catch {
      setPdfs([]);
    } finally {
      setPdfsLoading(false);
    }
  };

  const fetchWorkerDetails = async (phoneNumber) => {
    try {
      setDetailsLoading(true);
      setDetailsError("");
      const res = await axios.put(API_WORKER_DETAILS_URL, { phoneNumber },{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          });
      setWorkerDetails(res.data);
      setEditedPdfs(res.data.assigned_pdfs ? res.data.assigned_pdfs.split(", ") : []);
      setEditedWorkerName(res.data.worker_name || "");
      setEditedPhoneNumber(res.data.phone_number || "");
    } catch (err) {
      setDetailsError(
        err?.response?.data?.message || err.message || "Failed to fetch worker details"
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  Helper: build navigate state from assigned_pdfs
  // ══════════════════════════════════════════════════════════════
  const buildNavigateState = (clickedPdf, type) => {
    const allAssignedPdfs = (workerDetails.assigned_pdfs?.split(", ") || [])
      .filter(Boolean)
      .sort((a, b) => parseInt(a) - parseInt(b));

    return {
      clickedPdf,
      allAssignedPdfs,
      startRange: allAssignedPdfs[0],
      endRange: allAssignedPdfs[allAssignedPdfs.length - 1],
      workerName: workerDetails.worker_name,
      phoneNumber: workerDetails.phone_number,
      type,
    };
  };

  // ══════════════════════════════════════════════════════════════
  //  Validate worker info fields
  // ══════════════════════════════════════════════════════════════
  const validateWorkerInfo = () => {
    if (!editedWorkerName.trim()) {
      setWorkerInfoError("Worker name cannot be empty.");
      return false;
    }
    if (!/^\d{10,}$/.test(editedPhoneNumber.trim())) {
      setWorkerInfoError("Phone number must be numeric and at least 10 digits.");
      return false;
    }
    setWorkerInfoError("");
    return true;
  };

  // ══════════════════════════════════════════════════════════════
  //  UPDATE WORKER INFO ONLY
  // ══════════════════════════════════════════════════════════════
  const handleUpdateWorkerInfo = async () => {
    if (!validateWorkerInfo()) return;

    if (editedWorkerName.trim() === workerDetails.worker_name && 
        editedPhoneNumber.trim() === workerDetails.phone_number) {
      setWorkerUpdateMessage("No changes detected");
      setTimeout(() => setWorkerUpdateMessage(""), 3000);
      return;
    }

    try {
      setWorkerUpdateLoading(true);
      setWorkerUpdateMessage("");

      const payload = {
        oldPhoneNumber: workerDetails.phone_number,
        newWorkerName: editedWorkerName.trim(),
        newPhoneNumber: editedPhoneNumber.trim()
      };

      const response = await axios.put(API_UPDATE_WORKER_URL, payload,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          });
      
      setWorkerUpdateMessage(response.data.message || "Worker info updated successfully");
      console.log("server side response : ",response.data);
      
      await fetchAssignedWorkers();
      await fetchWorkerDetails(editedPhoneNumber.trim());
      
      setTimeout(() => setWorkerUpdateMessage(""), 3000);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err.message || "Error updating worker info";
      setWorkerUpdateMessage(`❌ ${errorMsg}`);
      setWorkerInfoError(errorMsg);
      setTimeout(() => {
        setWorkerUpdateMessage("");
        setWorkerInfoError("");
      }, 5000);
    } finally {
      setWorkerUpdateLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  UPDATE PDF BUNDLES ONLY
  // ══════════════════════════════════════════════════════════════
  const handleUpdatePdfBundles = async () => {
    if (!editedPdfs.length) {
      setPdfUpdateMessage("❌ Please assign at least one PDF bundle");
      setTimeout(() => setPdfUpdateMessage(""), 3000);
      return;
    }

    const currentPdfs = workerDetails.assigned_pdfs?.split(", ") || [];
    const hasChanges = editedPdfs.length !== currentPdfs.length ||
      editedPdfs.some(pdf => !currentPdfs.includes(pdf)) ||
      currentPdfs.some(pdf => !editedPdfs.includes(pdf));

    if (!hasChanges) {
      setPdfUpdateMessage("No changes detected in PDF assignments");
      setTimeout(() => setPdfUpdateMessage(""), 3000);
      return;
    }

    try {
      setPdfUpdateLoading(true);
      setPdfUpdateMessage("");

      const processed = workerDetails?.processed_pdfs?.split(", ") || [];
      const remaining = editedPdfs.filter(pdf => !processed.includes(pdf));

      const payload = {
        phoneNumber: workerDetails.phone_number,
        workerName: workerDetails.worker_name,
        assignedPdfs: editedPdfs.join(", "),
        pdfCount: editedPdfs.length,
        processedPdfs: workerDetails.processed_pdfs,
        remainingPdfs: remaining.join(", ")
      };

      const response = await axios.put(`${API_WORKER_DETAILS_URL}/update`, payload,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          });
      
      setPdfUpdateMessage(response.data.message || "PDF bundles updated successfully");
      
      await fetchAssignedWorkers();
      await fetchWorkerDetails(workerDetails.phone_number);
      
      setTimeout(() => setPdfUpdateMessage(""), 3000);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err.message || "Error updating PDF bundles";
      setPdfUpdateMessage(`❌ ${errorMsg}`);
      setTimeout(() => setPdfUpdateMessage(""), 5000);
    } finally {
      setPdfUpdateLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  DOWNLOAD 1 — Assigned Workers Excel
  // ══════════════════════════════════════════════════════════════
  const handleExcelDownload = () => {
    if (!assignedData.length) { alert("No data to download"); return; }
    setExcelDownloading(true);
    try {
      const grouped = {};
      assignedData.forEach((item, index) => {
        const key = item.phone_number;
        if (!grouped[key]) {
          grouped[key] = {
            id: item.id,
            worker_name: item.worker_name,
            phone_number: item.phone_number,
            bundles: new Set(),
            created_at: item.created_at,
          };
        }
        const bundleData = item.assigned_pdfs || item.assigned_bundles;
        if (bundleData !== undefined && bundleData !== null && bundleData !== "") {
          let parsedData = bundleData;
          if (typeof bundleData === "string" && (bundleData.startsWith('[') || bundleData.startsWith('{'))) {
            try { parsedData = JSON.parse(bundleData); } catch { parsedData = bundleData; }
          }
          const extractBundles = (data) => {
            const bundles = [];
            if (Array.isArray(data)) {
              data.forEach(bundle => {
                if (bundle !== null && bundle !== undefined) {
                  if (typeof bundle === "object" && bundle.id) bundles.push(String(bundle.id));
                  else if (typeof bundle === "object" && bundle.name) bundles.push(String(bundle.name));
                  else bundles.push(String(bundle).trim());
                }
              });
            } else if (typeof data === "string" && data.includes(",")) {
              data.split(",").forEach(b => { const c = b.trim(); if (c) bundles.push(c); });
            } else if (typeof data === "string" && data.trim()) {
              bundles.push(data.trim());
            } else if (typeof data === "number") {
              bundles.push(String(data));
            } else if (typeof data === "object") {
              if (data.bundle_id) bundles.push(String(data.bundle_id));
              if (data.bundle_name) bundles.push(String(data.bundle_name));
              if (data.id) bundles.push(String(data.id));
              Object.values(data).forEach(value => {
                if (typeof value === "string" || typeof value === "number") bundles.push(String(value));
              });
            }
            return bundles.filter(b => b && b !== "");
          };
          extractBundles(parsedData).forEach(bundle => {
            if (bundle && bundle !== "") grouped[key].bundles.add(bundle);
          });
        }
      });

      const rows = Object.values(grouped).map((item, i) => {
        const bundleArray = Array.from(item.bundles).sort();
        return {
          "S.No": i + 1,
          "ID": item.id,
          "Worker Name": item.worker_name || "—",
          "Phone Number": item.phone_number || "—",
          "Bundle Count": bundleArray.length,
          "Assigned Bundles": bundleArray.length ? bundleArray.join(", ") : "—",
          "Created At": item.created_at ? new Date(item.created_at).toLocaleString() : "—",
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const colWidths = Object.keys(rows[0] || {}).reduce((acc, key) => {
        const maxLength = Math.max(key.length, ...rows.map(row => String(row[key] || "").length));
        acc[key] = Math.min(maxLength + 2, 50);
        return acc;
      }, {});
      ws['!cols'] = Object.values(colWidths).map(width => ({ wch: width }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Assigned Workers");
      XLSX.writeFile(wb, `assigned-workers-${new Date().toISOString().split("T")[0]}.xlsx`);
    } catch (err) {
      alert(`Error generating Excel: ${err.message}`);
    } finally {
      setExcelDownloading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  DOWNLOAD 2 — Persons Data Excel
  // ══════════════════════════════════════════════════════════════
  const handlePersonsDownload = async () => {
    setPersonsDownloading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_ADMIN_API_URL}/persons/all`,{
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`
              }
            });
      const persons = Array.isArray(res.data?.data) ? res.data.data : [];
      if (!persons.length) { alert("कोई persons data नहीं मिला"); return; }

      const dash = (val) =>
        val !== undefined && val !== null && String(val).trim() !== "" ? String(val).trim() : "—";

      const parseJSON = (val) => {
        if (Array.isArray(val)) return val;
        try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
      };

      const EDU_ALIAS_MAP = {
        highschool: "highschool", "high school": "highschool", "10th": "highschool",
        intermediate: "intermediate", "12th": "intermediate",
        graduation: "graduation", "b.sc": "graduation", "b.a": "graduation",
        "b.com": "graduation", "b.tech": "graduation", "b.e": "graduation",
        "post graduation": "postgraduation", "m.sc": "postgraduation",
        "m.a": "postgraduation", "m.com": "postgraduation",
        "m.tech": "postgraduation", "m.e": "postgraduation",
        ccc: "ccc", "o-level": "ccc",
      };

      const resolveKey = (row) =>
        EDU_ALIAS_MAP[(row.exam ?? row.l ?? "").toLowerCase().trim()] ?? null;

      const extractEducationData = (eduMap, key) => {
        const row = eduMap[key];
        if (!row) return { 
          board: "—", 
          scoreType: "—", 
          obtainedMarks: "—", 
          totalMarks: "—", 
          percentage: "—" 
        };
        
        const obtained = row.obtainedMarks ?? row.obtain;
        const total = row.totalMarks ?? row.total;
        const percentage = row.percentage;
        const scoreType = row.scoreType ?? "Percentage";
        
        let obtainedDisplay = "—";
        let totalDisplay = "—";
        
        if (scoreType === "CGPA") {
          obtainedDisplay = obtained !== undefined && obtained !== null ? `${obtained}` : "—";
          totalDisplay = "10";
        } else if (scoreType === "Grade") {
          obtainedDisplay = obtained !== undefined && obtained !== null ? `Grade: ${obtained}` : "—";
        } else {
          obtainedDisplay = obtained !== undefined && obtained !== null ? `${obtained}` : "—";
          totalDisplay = total !== undefined && total !== null ? `${total}` : "—";
        }
        
        let percentageDisplay = "—";
        if (percentage !== undefined && percentage !== null) {
          percentageDisplay = `${percentage}`;
        }

        return {
          board: dash(row.board),
          scoreType: scoreType === "Percentage" ? "Percentage" : scoreType,
          obtainedMarks: obtainedDisplay,
          totalMarks: totalDisplay,
          percentage: percentageDisplay
        };
      };

      const normalizeWork = (w = {}) => ({
        organization: dash(w.organization ?? w.orgName ?? w.o),
        position: dash(w.post ?? w.p),
        duration: dash(w.duration ?? w.d),
        salary: dash(w.salary ?? w.s),
      });

      const finalRows = persons.map((p, i) => {
        const eduRows = parseJSON(p.edu_rows);
        const eduMap = {}, otherEdu = [];
        for (const row of eduRows) {
          const key = resolveKey(row);
          if (key && !eduMap[key]) eduMap[key] = row;
          else if (!key) otherEdu.push(row);
        }
        
        const highSchool = extractEducationData(eduMap, "highschool");
        const intermediate = extractEducationData(eduMap, "intermediate");
        const graduation = extractEducationData(eduMap, "graduation");
        const postGraduation = extractEducationData(eduMap, "postgraduation");
        const ccc = extractEducationData(eduMap, "ccc");
        
        const workRows = parseJSON(p.work_rows);
        const work1 = normalizeWork(workRows[0]);
        const work2 = normalizeWork(workRows[1]);
        
        let correspondenceAddress = dash(p.correspondence_address ?? p.correspondenceAddress);
        if (correspondenceAddress === "—" && p.address) correspondenceAddress = dash(p.address);

        const photoPath = p.photo_path ? p.photo_path : "—";
        const signaturePath = p.signature_path ? p.signature_path : "—";

        return {
          "S.No": i + 1,
          "Folder": dash(p.folder),
          "Post": dash(p.post),
          "Vibhag": dash(p.vibhag),
          "Name (English)": dash(p.name_english),
          "Name (Hindi)": dash(p.name_hindi),
          "Father's Name": dash(p.father_name),
          "DOB": dash(p.dob),
          "Gender": dash(p.gender),
          "Category": dash(p.category),
          "Religion": dash(p.religion),
          "Caste": dash(p.caste),
          "Mobile": dash(p.mobile),
          "Email": dash(p.email),
          "Aadhar Number": dash(p.aadhar_number),
          "Photo Path": photoPath,
          "Signature Path": signaturePath,
          "Current Address": dash(p.address),
          "Correspondence Address": correspondenceAddress,
          "Pin Code": dash(p.pin_code),
          "High School Board": highSchool.board,
          "High School Score Type": highSchool.scoreType,
          "High School Obtained Marks": highSchool.obtainedMarks,
          "High School Total Marks": highSchool.totalMarks,
          "High School Percentage": highSchool.percentage,
          "Intermediate Board": intermediate.board,
          "Intermediate Score Type": intermediate.scoreType,
          "Intermediate Obtained Marks": intermediate.obtainedMarks,
          "Intermediate Total Marks": intermediate.totalMarks,
          "Intermediate Percentage": intermediate.percentage,
          "Graduation Board": graduation.board,
          "Graduation Score Type": graduation.scoreType,
          "Graduation Obtained Marks": graduation.obtainedMarks,
          "Graduation Total Marks": graduation.totalMarks,
          "Graduation Percentage": graduation.percentage,
          "Post Graduation Board": postGraduation.board,
          "Post Graduation Score Type": postGraduation.scoreType,
          "Post Graduation Obtained Marks": postGraduation.obtainedMarks,
          "Post Graduation Total Marks": postGraduation.totalMarks,
          "Post Graduation Percentage": postGraduation.percentage,
          "CCC Board": ccc.board,
          "CCC Score Type": ccc.scoreType,
          "CCC Obtained Marks": ccc.obtainedMarks,
          "CCC Total Marks": ccc.totalMarks,
          "CCC Percentage": ccc.percentage,
          "Other Education": otherEdu.length
            ? otherEdu.map(e => `${e.exam ?? e.l}: ${dash(e.board)}`).join(" | ")
            : "—",
          "Hindi Typing": dash(p.hindi_typing),
          "English Typing": dash(p.english_typing),
          "Shorthand Speed": dash(p.shorthand_speed),
          "Work 1 Organization": work1.organization,
          "Work 1 Position": work1.position,
          "Work 1 Duration": work1.duration,
          "Work 1 Salary": work1.salary,
          "Work 2 Organization": work2.organization,
          "Work 2 Position": work2.position,
          "Work 2 Duration": work2.duration,
          "Work 2 Salary": work2.salary,
          "Payment Method": dash(p.payment_method),
          "Payment Number": dash(p.payment_number),
          "Created At": p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "—",
          "Updated At": p.updated_at ? new Date(p.updated_at).toLocaleString("en-IN") : "—",
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(finalRows);
      
      ws["!cols"] = Object.keys(finalRows[0]).map(key => ({
        wch: Math.min(Math.max(key.length + 2, 12), 40),
      }));
      
      XLSX.utils.book_append_sheet(wb, ws, "Persons");
      XLSX.writeFile(wb, `persons-data-${new Date().toISOString().split("T")[0]}.xlsx`);
      
    } catch (err) {
      console.error("handlePersonsDownload error:", err);
      alert("Export failed. Please try again.");
    } finally {
      setPersonsDownloading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  Row expand / edit helpers
  // ══════════════════════════════════════════════════════════════
  const handleRowClick = async (item) => {
    if (expandedRow === item.id) {
      setExpandedRow(null);
      setWorkerDetails(null);
      setEditMode(false);
      setNewPdfRange({ start: "", end: "" });
      setWorkerInfoError("");
      setWorkerUpdateMessage("");
      setPdfUpdateMessage("");
    } else {
      setExpandedRow(item.id);
      await fetchWorkerDetails(item.phone_number);
      setEditMode(false);
      setNewPdfRange({ start: "", end: "" });
      setWorkerInfoError("");
      setWorkerUpdateMessage("");
      setPdfUpdateMessage("");
    }
  };

  // ── Enter edit mode: pre-fill all editable fields ─────────────
  const handleEnterEditMode = () => {
    setEditMode(true);
    setEditedWorkerName(workerDetails.worker_name || "");
    setEditedPhoneNumber(workerDetails.phone_number || "");
    setEditedPdfs(workerDetails.assigned_pdfs ? workerDetails.assigned_pdfs.split(", ") : []);
    setNewPdfRange({ start: "", end: "" });
    setWorkerInfoError("");
    setWorkerUpdateMessage("");
    setPdfUpdateMessage("");
  };

  // ── Cancel edit mode: revert all changes ──────────────────────
  const handleCancelEditMode = () => {
    setEditMode(false);
    setEditedWorkerName(workerDetails.worker_name || "");
    setEditedPhoneNumber(workerDetails.phone_number || "");
    setEditedPdfs(workerDetails.assigned_pdfs?.split(", ") || []);
    setNewPdfRange({ start: "", end: "" });
    setWorkerInfoError("");
    setRangeError("");
    setWorkerUpdateMessage("");
    setPdfUpdateMessage("");
  };

  const handleAddPdfs = () => {
    if (!newPdfRange.start || !newPdfRange.end) { alert("Please enter both start and end values"); return; }
    if (rangeError) { alert("Please fix range errors"); return; }
    const s = parseInt(newPdfRange.start);
    const e = parseInt(newPdfRange.end);
    const newPdfs = [];
    for (let i = s; i <= e; i++) newPdfs.push(i.toString());
    const existingSet = new Set(editedPdfs);
    const dupes = newPdfs.filter(p => existingSet.has(p));
    if (dupes.length) { alert(`PDFs ${dupes.join(", ")} are already assigned`); return; }
    setEditedPdfs([...editedPdfs, ...newPdfs].sort((a, b) => parseInt(a) - parseInt(b)));
    setNewPdfRange({ start: "", end: "" });
  };

  const handleRemovePdf = (pdf) => setEditedPdfs(editedPdfs.filter(p => p !== pdf));

  // ══════════════════════════════════════════════════════════════
  //  Form helpers
  // ══════════════════════════════════════════════════════════════
  const totalWorkers = useMemo(() => assignedData.length, [assignedData]);

  const totalPdfsAssigned = useMemo(
    () =>
      assignedData.reduce(
        (sum, item) =>
          sum + (Array.isArray(item.assigned_bundles) ? item.assigned_bundles.length : 1),
        0
      ),
    [assignedData]
  );

  const handleChange           = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleRangeInputChange = e => setRangeInputs(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const resetForm = () => {
    setFormData({ workerName: "", phoneNumber: "", pdfCount: 0, assignedPdfs: [], status: "Assigned" });
    setRangeInputs({ startPdf: "", endPdf: "" });
    setRangeError("");
    setGeneratedRange([]);
  };

  const handleAssignWorker = async (e) => {
    e.preventDefault();
    if (!formData.workerName.trim() || !formData.phoneNumber.trim()) { alert("Please fill all required fields"); return; }
    if (!formData.assignedPdfs.length) { alert("Please enter a valid PDF range"); return; }
    if (rangeError) { alert("Please fix range errors before submitting"); return; }
    try {
      setSubmitLoading(true);
      const payload = {
        workerName:   formData.workerName,
        phoneNumber:  formData.phoneNumber,
        pdfCount:     formData.pdfCount,
        assignedPdfs: formData.assignedPdfs.join(", "),
      };
      const res = await axios.post(API_POST_URL, payload,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          });
      alert(res.data.message || "Worker assigned successfully");
      resetForm();
      setShowAssignForm(false);
      fetchAssignedWorkers();
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Error while saving data");
    } finally {
      setSubmitLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="admin-panel">

      {/* ── Header ── */}
      <div className="admin-header">
        <div>
          <h1>Admin Panel</h1>
          <p>Assign PDF Numbers to Workers</p>
        </div>

        <div className="header-btns">
          <button className="upload-nav-btn1" onClick={() => navigate("/FolderLogin")}>
            Go To Folder
          </button>
          <button className="upload-nav-btn" onClick={() => navigate("/upload")}>
            Go To Upload
          </button>
          <button className="assign-btn" onClick={() => setShowAssignForm(!showAssignForm)}>
            {showAssignForm ? "Close Form" : "Assign PDFs"}
          </button>
          <button
            className="excel-download-btn"
            onClick={handleExcelDownload}
            disabled={excelDownloading || !assignedData.length}
            title="Assigned Workers ka Excel download karo"
          >
            {excelDownloading
              ? <><span className="btn-spinner"></span> Downloading...</>
              : "📥 Download Assigned Workers"}
          </button>
          <button
            className="persons-download-btn"
            onClick={handlePersonsDownload}
            disabled={personsDownloading}
            title="Persons table ka poora data Excel mein download karo"
          >
            {personsDownloading
              ? <><span className="btn-spinner"></span> Downloading...</>
              : "👥 Download Persons Data"}
          </button>
          <button className="refresh-btn" onClick={fetchAssignedWorkers}>🔄 Refresh</button>
          <button
            onClick={() => {
              sessionStorage.removeItem("AdminLogin");
              sessionStorage.removeItem("admin");
              sessionStorage.removeItem("token");
              navigate("/adminP")
            }}
            style={{
              backgroundColor: "red",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            Log out
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Workers</h3>
          <p>{totalWorkers}</p>
        </div>
        <div className="stat-card" onClick={() => navigate("/AssignedPDF")}>
          <h3>Total PDFs Assigned</h3>
          <p>Click here</p>
        </div>
        <div className="stat-card" onClick={() => navigate("/AvailabelPDF")}>
          <h3>See Processed PDFs</h3>
          <p>Click here</p>
        </div>
      </div>

      {/* ── Assign Form ── */}
      {showAssignForm && (
        <div className="section">
          <h2>Assign PDF Range to Worker</h2>
          <form className="assign-form" onSubmit={handleAssignWorker}>
            <div className="form-grid">
              <div className="form-group">
                <label>Worker Name</label>
                <input
                  type="text" name="workerName" value={formData.workerName}
                  onChange={handleChange} placeholder="Enter worker name" required
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text" name="phoneNumber" value={formData.phoneNumber}
                  onChange={handleChange} placeholder="Enter phone number" required
                />
              </div>
              <div className="form-group">
                <label>Start PDF Number</label>
                <input
                  type="number" name="startPdf" value={rangeInputs.startPdf}
                  onChange={handleRangeInputChange} placeholder="e.g., 1001" min="1"
                />
              </div>
              <div className="form-group">
                <label>End PDF Number</label>
                <input
                  type="number" name="endPdf" value={rangeInputs.endPdf}
                  onChange={handleRangeInputChange} placeholder="e.g., 1050" min="1"
                />
              </div>

              {rangeError && (
                <div className="error-message full-width">⚠️ {rangeError}</div>
              )}

              {generatedRange.length > 0 && !rangeError && (
                <div className="range-preview full-width">
                  <div className="preview-header">
                    <h4>PDF Numbers to Assign</h4>
                    <span className="bundle-count">Total: {generatedRange.length} PDFs</span>
                  </div>
                  <div className="preview-chips">
                    {generatedRange.slice(0, 10).map((pdf, i) => (
                      <span key={i} className="pdf-chip preview-chip">{pdf}</span>
                    ))}
                    {generatedRange.length > 10 && (
                      <span className="pdf-chip preview-chip more-chip">
                        +{generatedRange.length - 10} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Number of PDFs</label>
                <input type="number" name="pdfCount" value={formData.pdfCount} readOnly placeholder="Auto count" />
              </div>
            </div>

            <button
              type="submit" className="submit-btn"
              disabled={submitLoading || !formData.assignedPdfs.length || !!rangeError}
            >
              {submitLoading ? "Saving..." : "Save Assignment"}
            </button>
          </form>
        </div>
      )}

      {/* ── Workers Table ── */}
      <div className="section">
        <h2>Assigned Workers Table</h2>
        <div className="table-wrapper">
          <table className="assigned-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Worker Name</th>
                <th>Phone Number</th>
                <th>PDF Count</th>
                <th>Assigned PDF Numbers</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="table-message">Loading data...</td></tr>
              ) : error ? (
                <tr><td colSpan="7" className="table-message error-text">{error}</td></tr>
              ) : assignedData.length > 0 ? (
                assignedData.map(item => (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`clickable-row ${expandedRow === item.id ? "expanded" : ""}`}
                      onClick={() => handleRowClick(item)}
                    >
                      <td className="expand-icon">{expandedRow === item.id ? "▼" : "▶"}</td>
                      <td>{item.id}</td>
                      <td>{item.worker_name}</td>
                      <td>{item.phone_number}</td>
                      <td>{item.assigned_bundles.length || 0}</td>
                      <td className="assigned-pdfs-cell">
                        {Array.isArray(item.assigned_bundles)
                          ? item.assigned_bundles.join(", ")
                          : item.assigned_bundles}
                      </td>
                      <td>{new Date(item.created_at).toLocaleString()}</td>
                    </tr>

                    {expandedRow === item.id && (
                      <tr className="expanded-row">
                        <td colSpan="7">
                          <div className="expanded-content">
                            <h3>Worker Details</h3>

                            {detailsLoading && (
                              <div className="loading-spinner">Loading details...</div>
                            )}
                            {detailsError && (
                              <div className="error-message">{detailsError}</div>
                            )}

                            {workerDetails && !detailsLoading && (
                              <div className="worker-details">

                                {/* ── Details Grid: editable in edit mode ── */}
                                <div className="details-grid">

                                  {/* Worker Name */}
                                  <div className="detail-item">
                                    <label>Worker Name:</label>
                                    {editMode ? (
                                      <input
                                        type="text"
                                        className="edit-inline-input"
                                        value={editedWorkerName}
                                        onChange={e => setEditedWorkerName(e.target.value)}
                                        placeholder="Enter worker name"
                                      />
                                    ) : (
                                      <span>{workerDetails.worker_name}</span>
                                    )}
                                  </div>

                                  {/* Phone Number */}
                                  <div className="detail-item">
                                    <label>Phone Number:</label>
                                    {editMode ? (
                                      <input
                                        type="text"
                                        className="edit-inline-input"
                                        value={editedPhoneNumber}
                                        onChange={e => setEditedPhoneNumber(e.target.value)}
                                        placeholder="Enter phone number (min 10 digits)"
                                        maxLength={15}
                                      />
                                    ) : (
                                      <span>{workerDetails.phone_number}</span>
                                    )}
                                  </div>

                                  <div className="detail-item">
                                    <label>Status:</label>
                                    <span className="status-badge">{workerDetails.status}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>Total Assigned:</label>
                                    <span>{workerDetails.pdf_count}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>Update Details</label>
                                    <button
                                          onClick={handleUpdateWorkerInfo}
                                          className="update-worker-btn"
                                          disabled={workerUpdateLoading}
                                        >
                                          {workerUpdateLoading ? "Updating..." : "👤 Update Worker Info"}
                                        </button>
                                  </div>
                                </div>

                                {/* Validation error for worker info */}
                                {workerInfoError && (
                                  <div className="error-message" style={{ marginBottom: "12px" }}>
                                    ⚠️ {workerInfoError}
                                  </div>
                                )}

                                {/* Success/Error Messages */}
                                {workerUpdateMessage && (
                                  <div className={`message ${workerUpdateMessage.includes("❌") ? "error-message" : "success-message"}`} 
                                       style={{ marginBottom: "12px", padding: "8px", borderRadius: "4px" }}>
                                    {workerUpdateMessage}
                                  </div>
                                )}
                                
                                {pdfUpdateMessage && (
                                  <div className={`message ${pdfUpdateMessage.includes("❌") ? "error-message" : "success-message"}`}
                                       style={{ marginBottom: "12px", padding: "8px", borderRadius: "4px" }}>
                                    {pdfUpdateMessage}
                                  </div>
                                )}

                                <div className="pdf-sections">

                                  {/* ── Processed PDFs ── */}
                                  <div className="pdf-section">
                                    <h4>✅ Processed PDFs</h4>
                                    <div className="pdf-list">
                                      {(workerDetails.processed_pdfs?.split(", ") || [])
                                        .filter(Boolean)
                                        .sort((a, b) => parseInt(a) - parseInt(b))
                                        .map((pdf, idx) => (
                                          <span
                                            key={idx}
                                            className="pdf-chip processed"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                              const allAssignedPdfs = (workerDetails.assigned_pdfs?.split(", ") || [])
                                                .filter(Boolean)
                                                .sort((a, b) => parseInt(a) - parseInt(b));
                                              navigate("/Result", {
                                                state: {
                                                  clickedPdf: pdf,
                                                  allAssignedPdfs,
                                                  startRange: allAssignedPdfs[0],
                                                  endRange: allAssignedPdfs[allAssignedPdfs.length - 1],
                                                  workerName: workerDetails.worker_name,
                                                  phoneNumber: workerDetails.phone_number,
                                                  type: "processed",
                                                },
                                              });
                                            }}
                                          >
                                            {pdf}
                                          </span>
                                        ))}
                                      {(!workerDetails.processed_pdfs || workerDetails.processed_pdfs === "") && (
                                        <p className="empty-message">No PDFs processed yet</p>
                                      )}
                                    </div>
                                  </div>

                                  {/* ── Remaining PDFs ── */}
                                  <div className="pdf-section">
                                    <h4>⏳ Remaining PDFs</h4>
                                    <div className="pdf-list">
                                      {(workerDetails.remaining_pdfs?.split(", ") || [])
                                        .filter(Boolean)
                                        .sort((a, b) => parseInt(a) - parseInt(b))
                                        .map((pdf, idx) => (
                                          <span
                                            key={idx}
                                            className="pdf-chip remaining"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                              const allAssignedPdfs = (workerDetails.assigned_pdfs?.split(", ") || [])
                                                .filter(Boolean)
                                                .sort((a, b) => parseInt(a) - parseInt(b));
                                              navigate("/Result", {
                                                state: {
                                                  clickedPdf: pdf,
                                                  allAssignedPdfs,
                                                  startRange: allAssignedPdfs[0],
                                                  endRange: allAssignedPdfs[allAssignedPdfs.length - 1],
                                                  workerName: workerDetails.worker_name,
                                                  phoneNumber: workerDetails.phone_number,
                                                  type: "remaining",
                                                },
                                              });
                                            }}
                                          >
                                            {pdf}
                                          </span>
                                        ))}
                                      {(!workerDetails.remaining_pdfs || workerDetails.remaining_pdfs === "") && (
                                        <p className="empty-message">No remaining PDFs</p>
                                      )}
                                    </div>
                                  </div>

                                  {/* ── Edit Mode / All Assigned ── */}
                                  {editMode ? (
                                    <div className="pdf-section edit-mode">
                                      <h4>✏️ All Assigned PDFs (Edit Mode)</h4>
                                      <div className="add-pdf-range">
                                        <h5>Add PDF Range</h5>
                                        <div className="range-inputs">
                                          <input
                                            type="number" placeholder="Start"
                                            value={newPdfRange.start}
                                            onChange={e => setNewPdfRange({ ...newPdfRange, start: e.target.value })}
                                          />
                                          <span>to</span>
                                          <input
                                            type="number" placeholder="End"
                                            value={newPdfRange.end}
                                            onChange={e => setNewPdfRange({ ...newPdfRange, end: e.target.value })}
                                          />
                                          <button onClick={handleAddPdfs} className="add-range-btn">
                                            Add Range
                                          </button>
                                        </div>
                                        {rangeError && (
                                          <div className="range-error">{rangeError}</div>
                                        )}
                                      </div>

                                      <div className="editable-pdf-list">
                                        {editedPdfs.map((pdf, idx) => (
                                          <span key={idx} className="pdf-chip editable">
                                            {pdf}
                                            <button
                                              className="remove-pdf-btn"
                                              onClick={() => handleRemovePdf(pdf)}
                                              title="Remove"
                                            >
                                              ×
                                            </button>
                                          </span>
                                        ))}
                                      </div>

                                      <div className="edit-actions">
                                        <button
                                          onClick={handleUpdatePdfBundles}
                                          className="update-pdfs-btn"
                                          disabled={pdfUpdateLoading}
                                        >
                                          {pdfUpdateLoading ? "Updating..." : "📄 Update PDF Bundles"}
                                        </button>
                                        <button
                                          onClick={handleCancelEditMode}
                                          className="cancel-btn"
                                          disabled={workerUpdateLoading || pdfUpdateLoading}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="pdf-section">
                                      <div className="section-header">
                                        <h4>📋 All Assigned PDFs</h4>
                                        <button onClick={handleEnterEditMode} className="edit-btn">
                                          Edit Assignments
                                        </button>
                                      </div>
                                      <div className="pdf-list">
                                        {(workerDetails.assigned_pdfs?.split(", ") || [])
                                          .filter(Boolean)
                                          .sort((a, b) => parseInt(a) - parseInt(b))
                                          .map((pdf, idx) => (
                                            <span
                                              key={idx}
                                              className="pdf-chip"
                                              style={{ cursor: "pointer" }}
                                              onClick={() => {
                                                const allAssignedPdfs = (workerDetails.assigned_pdfs?.split(", ") || [])
                                                  .filter(Boolean)
                                                  .sort((a, b) => parseInt(a) - parseInt(b));
                                                navigate("/Result", {
                                                  state: {
                                                    clickedPdf: pdf,
                                                    allAssignedPdfs,
                                                    startRange: allAssignedPdfs[0],
                                                    endRange: allAssignedPdfs[allAssignedPdfs.length - 1],
                                                    workerName: workerDetails.worker_name,
                                                    phoneNumber: workerDetails.phone_number,
                                                    type: "assigned",
                                                  },
                                                });
                                              }}
                                            >
                                              {pdf}
                                            </span>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr><td colSpan="7" className="table-message">No assigned worker data found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;